import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

type optionType = {
  item: JSX.Element;
  closeWhenClicked: boolean;
  onSelect: () => void;
  disabled?: boolean;
};

type FixDirection = 'right-top' | 'right-bottom' | 'left-top' | 'left-bottom';

type CustomDropdownProps = {
  button: ReactNode;
  options: Array<optionType> | any;
  disabled?: boolean;
  buttonClassName?: string;
  dropdownClassName?: string;
  dataTestId?: string;
  isFixed?: boolean;
  fixDirection?: FixDirection;
  dropdownWrapper?: string;
};

const CustomDropdown: FC<CustomDropdownProps> = ({
  button,
  options,
  buttonClassName = '',
  dropdownClassName = '',
  disabled = false,
  dataTestId,
  isFixed = false,
  fixDirection = 'right-top',
  dropdownWrapper = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const anchorRectRef = useRef<DOMRect | null>(null);

  const calculatePosition = useCallback(() => {
    const anchor = anchorRectRef.current;
    if (!anchor) return;

    const menuWidth = menuRef.current?.offsetWidth || 0;
    const menuHeight = menuRef.current?.offsetHeight || 0;

    const [horizontal, vertical] = fixDirection.split('-') as [
      'right' | 'left',
      'top' | 'bottom',
    ];

    let left: number;
    if (horizontal === 'right') {
      left = anchor.right - menuWidth;
    } else {
      left = anchor.left;
    }

    let top: number;
    if (vertical === 'top') {
      top = anchor.bottom + 4;
    } else {
      top = anchor.top - menuHeight - 4;
    }

    const viewportWidth = document.documentElement.clientWidth;
    if (left < 0) left = 4;
    if (left + menuWidth > viewportWidth) {
      left = viewportWidth - menuWidth - 4;
    }

    const viewportHeight = document.documentElement.clientHeight;
    if (top < 0) top = 4;
    if (top + menuHeight > viewportHeight) {
      top = viewportHeight - menuHeight - 4;
    }

    setDropdownPos({ top, left });
  }, [fixDirection]);

  const toggleDropdown = (e) => {
    e.stopPropagation();
    if (!isOpen && buttonRef.current) {
      anchorRectRef.current = buttonRef.current.getBoundingClientRect();
    }
    setIsOpen((prev) => !prev);
  };

  const handleOptionClick = (option: optionType) => {
    if (option.disabled) return;
    if (option.closeWhenClicked) setIsOpen(false);
    option.onSelect();
  };

  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      const target = event.target as Node;
      const clickedInsideDropdown =
        dropdownRef.current && dropdownRef.current.contains(target);
      const clickedInsideMenu =
        isFixed && menuRef.current && menuRef.current.contains(target);

      if (!clickedInsideDropdown && !clickedInsideMenu) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFixed]);

  useEffect(() => {
    if (!isFixed) return;

    if (isOpen) {
      calculatePosition();
    } else {
      setDropdownPos(null);
      anchorRectRef.current = null;
    }
  }, [isOpen, isFixed, calculatePosition]);

  useEffect(() => {
    if (!isFixed || !isOpen || !menuRef.current) return;

    const observer = new ResizeObserver(() => {
      calculatePosition();
    });

    observer.observe(menuRef.current);

    return () => observer.disconnect();
  }, [isFixed, isOpen, calculatePosition]);

  const menuStyle: React.CSSProperties = isFixed
    ? {
        position: 'fixed',
        top: dropdownPos?.top ?? -9999,
        left: dropdownPos?.left ?? -9999,
        visibility: dropdownPos ? 'visible' : 'hidden',
      }
    : {};

  return (
    <div
      className={`dropdown ${dropdownWrapper}`}
      ref={dropdownRef}
      data-test-id={dataTestId}
    >
      <button
        ref={buttonRef}
        data-test-id={`${dataTestId}-button`}
        className={buttonClassName || ''}
        onClick={toggleDropdown}
        disabled={disabled}
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
        }}
      >
        {button}
      </button>
      {isOpen && (
        <div
          ref={menuRef}
          className={`dropdown-menu ${dropdownClassName || ''}`}
          style={menuStyle}
        >
          {options?.map((option, index) => {
            return (
              <div
                data-test-id={`dropdown-item-${index}`}
                key={index}
                className={`dropdown-item ${option.disabled ? 'disabled' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOptionClick(option);
                }}
              >
                {option.item}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomDropdown;
